import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataList, StatusPill, type DataListColumn } from "@ozpanel/kit";
import { Button, ConfirmDialog, Section, Stat, useAuth, useToast } from "@ozpanel/ui";

/**
 * What Podman is actually doing on this node.
 *
 * Podman is the platform's ONE container runtime — daemonless, and each tenant's container is
 * user-namespaced into that tenant's own id range (so a container-root write to the shared home
 * lands owned by the tenant). App lifecycle and placement live on the Apps screens; this panel is
 * the operator dashboard for the ENGINE underneath them: version and storage, containers broken
 * down by state, the disk it holds and what a prune would reclaim, this node's declared role, and —
 * the question a runtime view should answer that an app list does not — exactly which containers are
 * running HERE right now and which tenant OWNS each.
 */

type Disk = { type: string; total?: string; active?: string; size?: string; reclaimable?: string };
type Status = {
  ok: boolean;
  node?: string;
  version?: string;
  runtime?: string;
  storage_driver?: string;
  graph_root?: string;
  containers?: number;
  images?: number;
  by_state?: Record<string, number>;
  disk?: Disk[] | null;
  role?: { am_primary?: boolean; am_successor?: boolean; declared_primary?: string; seq?: number };
  error?: string;
};
type Container = { name: string; owner?: string; image?: string; state?: string; status?: string };

function stateTone(s?: string): "ok" | "bad" | "warn" | "neutral" {
  switch (s) {
    case "running":
      return "ok";
    case "exited":
    case "stopped":
    case "created":
      return "neutral";
    case "dead":
      return "bad";
    default:
      return "warn";
  }
}

function byStateHint(by?: Record<string, number>): string | undefined {
  if (!by) return undefined;
  const parts = Object.entries(by).map(([k, v]) => `${v} ${k}`);
  return parts.length ? parts.join(" · ") : undefined;
}

function roleText(role?: Status["role"]): string {
  if (!role) return "—";
  if (role.am_primary) return "primary — the workload runs here";
  if (role.declared_primary) return `member — primary is ${role.declared_primary}`;
  if (role.am_successor) return "successor";
  return "—";
}

export function PodmanOps() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [confirmPrune, setConfirmPrune] = React.useState(false);

  const status = useQuery({
    queryKey: ["podman-status"],
    queryFn: () => client.apiFetch<Status>("/podman/status"),
    refetchInterval: 30000,
  });
  const conts = useQuery({
    queryKey: ["podman-containers"],
    queryFn: () => client.apiFetch<{ containers: Container[] | null; node?: string }>("/podman/containers"),
    refetchInterval: 30000,
  });

  const s = status.data;

  const prune = () => {
    setConfirmPrune(false);
    client
      .apiFetch<{ pruned: boolean; output?: string }>("/podman/prune", { method: "POST" })
      .then((d) => {
        notify({ kind: "success", title: "Reclaimed unused images", body: d.output || undefined });
        void qc.invalidateQueries({ queryKey: ["podman-status"] });
      })
      .catch((e: unknown) => notify({ kind: "error", title: "Prune failed", body: e instanceof Error ? e.message : String(e) }));
  };

  const cols: DataListColumn<Container>[] = [
    { key: "name", label: "Container", render: (c) => <span className="font-mono text-[12px]">{c.name}</span> },
    { key: "owner", label: "Owner", value: (c) => c.owner || "—" },
    { key: "image", label: "Image", render: (c) => <span className="font-mono text-[12px] text-muted">{c.image || "—"}</span> },
    { key: "state", label: "State", render: (c) => <StatusPill tone={stateTone(c.state)}>{c.state || "?"}</StatusPill> },
    { key: "status", label: "Status", value: (c) => c.status || "" },
  ];

  return (
    <div className="space-y-4">
      <Section
        title="Runtime"
        hint={s?.node ? `node ${s.node}` : undefined}
        actions={
          <Button size="sm" variant="ghost" onClick={() => setConfirmPrune(true)}>
            Reclaim disk
          </Button>
        }
      >
        {s && !s.ok ? (
          <div className="text-[13px] text-crit">The Podman runtime is not answering{s.error ? `: ${s.error}` : ""}.</div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Stat label="Version" value={s?.version || "—"} />
            <Stat label="OCI runtime" value={s?.runtime || "—"} />
            <Stat label="Storage" value={s?.storage_driver || "—"} hint={s?.graph_root} />
            <Stat label="Containers" value={String(s?.containers ?? 0)} hint={byStateHint(s?.by_state)} />
            <Stat label="Images" value={String(s?.images ?? 0)} />
            <Stat label="Role" value={roleText(s?.role)} tone={s?.role?.am_primary ? "accent" : undefined} />
          </div>
        )}
      </Section>

      {s?.disk && s.disk.length > 0 && (
        <Section title="Disk" hint="what a prune would reclaim">
          <div className="flex flex-wrap gap-3">
            {s.disk.map((d) => (
              <Stat key={d.type} label={d.type} value={d.size || "—"} hint={d.reclaimable ? `reclaimable ${d.reclaimable}` : undefined} />
            ))}
          </div>
        </Section>
      )}

      <Section title="Containers on this node" hint={conts.data?.node ? `node ${conts.data.node}` : undefined}>
        <DataList
          items={conts.data?.containers || []}
          columns={cols}
          getRowKey={(c) => c.name}
          loading={conts.isLoading}
          error={conts.error}
          emptyMessage="No containers running on this node."
          emptyHint="Tenant apps placed on another node run there, not here."
        />
      </Section>

      <ConfirmDialog
        open={confirmPrune}
        onOpenChange={setConfirmPrune}
        title="Reclaim disk?"
        body="Removes container images that no container is using. Tenant data volumes and running containers are never touched — a removed image is simply re-pulled on the next start."
        confirmLabel="Reclaim"
        onConfirm={prune}
      />
    </div>
  );
}
